import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WorkflowEditorService } from '../../../store/workflow-editor.service';
import { WorkflowVariable, WorkflowVariableSource } from '../../../models/workflow-variable-catalog';

interface VariableGroup {
  source: WorkflowVariableSource;
  label: string;
  variables: WorkflowVariable[];
}

/** Etiquetas por origen. El objeto base va primero: es lo que el usuario busca antes. */
const SOURCE_LABELS: Record<WorkflowVariableSource, string> = {
  OBJECT: 'Registro inscrito',
  ENROLLMENT: 'Contexto del workflow',
  STEP_OUTPUT: 'Salida del paso anterior'
};

const SOURCE_ORDER: WorkflowVariableSource[] = ['OBJECT', 'ENROLLMENT', 'STEP_OUTPUT'];

/**
 * Botón `{{ }}` que inserta un token en la posición del cursor del campo destino.
 *
 * Se calca del `variable-picker` de chatflow y se adapta a workflow: los tokens salen del
 * catálogo derivado del `objectType` del workflow (`WorkflowEditorService.variables`), no de
 * un endpoint del backend (ver GAP DE API). El token viaja SIN llaves; aquí se envuelve en
 * `{{…}}`.
 *
 * Gotcha §6: no inserta nada en solo lectura (ACTIVE/ARCHIVED) — el botón queda deshabilitado
 * y `valueChange` no emite. Insertar un token es EDITAR la config; el que lo consume debe
 * escribir por el store (`updateNodeConfig`) para no romper el autosave.
 */
@Component({
  selector: 'app-wf-variable-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './variable-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowVariablePickerComponent {
  private readonly editor = inject(WorkflowEditorService);
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Campo en el que se insertará el token. */
  readonly target = input<HTMLInputElement | HTMLTextAreaElement | null>(null);
  readonly disabled = input(false);

  /** Nuevo valor del campo tras insertar el token. */
  readonly valueChange = output<string>();

  protected readonly open = signal(false);

  /** Las llaves se construyen en TS: escritas en plantilla, Angular las tomaría por interpolación. */
  protected readonly bracesLabel = '{{ }}';

  protected tokenPreview(variable: WorkflowVariable): string {
    return `{{${variable.token}}}`;
  }

  protected readonly groups = computed<VariableGroup[]>(() => {
    const variables = this.editor.variables();
    return SOURCE_ORDER.map((source) => ({
      source,
      label: SOURCE_LABELS[source],
      variables: variables.filter((variable) => variable.source === source)
    })).filter((group) => group.variables.length > 0);
  });

  protected readonly hasVariables = computed(() => this.editor.variables().length > 0);

  protected toggle(): void {
    if (!this.disabled()) {
      this.open.update((value) => !value);
    }
  }

  /** Cierra el panel al hacer clic fuera. */
  protected onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as globalThis.Node)) {
      this.open.set(false);
    }
  }

  /**
   * Inserta `{{token}}` donde está el cursor (o reemplazando la selección) y deja el caret
   * justo después del token insertado.
   */
  protected insert(variable: WorkflowVariable): void {
    const field = this.target();
    const token = `{{${variable.token}}}`;
    if (!field) {
      this.open.set(false);
      return;
    }
    const value = field.value ?? '';
    const start = field.selectionStart ?? value.length;
    const end = field.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;

    field.value = next;
    const caret = start + token.length;
    field.setSelectionRange(caret, caret);
    field.focus();

    this.valueChange.emit(next);
    this.open.set(false);
  }
}
